import { Box, Container } from "@mui/material";
import { useState } from "react";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import api from "../api";

import Collection from "../components/Collection";
import Loader from "../components/Loader";
import notify from "../components/Notification/helper";
import PageHeader from "../components/PageHeader";
import Cuisine from "../layouts/Cuisine";
import Cuisines from "../layouts/Cuisines";

import { getTranslation } from "../utils/translate";
import { useUpdateDocumentMeta } from "../hooks/useUpdateDocumentMeta";

import "../styles/Collection.scss";
import { pushToGoogleAnalytics } from "../utils/google-analytics";

const isCollectionUuid = (value) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value || "",
  );

function CollectionPage() {
  const { slug } = useParams();

  const language = useSelector((state) => state.language);

  const [collection, setCollection] = useState({});
  const [loading, setLoading] = useState(false);
  const [beefCuts, setBeefCuts] = useState([]);
  const [cuisines, setCuisines] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [recipeAuthors, setRecipeAuthors] = useState([]);

  const apiFieldsToFetch = [
    "icon",
    "id",
    "items.collection",
    "items.id",
    "items.item.*",
    "items.item.*.*",
    "items.sort",
    "items.item:beef_product.slug",
    "items.item:beef_product.beef_product_overrides.id",
    "items.item:beef_product.beef_product_overrides.destination",
    "items.item:beef_product.beef_product_overrides.retailer",
    "items.item:beef_product.beef_product_overrides.status",
    "items.item:beef_product.beef_product_overrides.translations.*",
    "items.item:recipe.category.status",
    "items.item:recipe.category.translations.*",
    "items.item:recipe.retailers.retailer_id",
    "items.item:recipe.retailers.sort",
    "items.item:recipe.items.*",
    "items.item:recipe.imagegallery.*",
    "items.item:recipe.layout_translations.*",
    "items.item:beef_product.translations.biography",
    "items.item:beef_product.translations.content",
    "items.item:beef_product.translations.external",
    "items.item:beef_product.translations.image",
    "items.item:beef_product.translations.label",
    "items.item:beef_product.translations.languages_code",
    "items.item:beef_product.translations.languages_id",
    "items.item:beef_product.translations.name",
    "items.item:beef_product.translations.title",
    "items.item:beef_product.translations.url",
    "slug",
    "sort",
    "translations.*",
    "type",
  ];

  useUpdateDocumentMeta();

  useEffect(async () => {
    try {
      window.scrollTo(0, 0);
      setLoading(true);

      let collectionRecordId = slug;

      if (!isCollectionUuid(slug)) {
        const matches = await api.searchCollections(
          {
            filter: {
              slug: {
                _eq: slug,
              },
            },
            limit: 1,
          },
          ["id"],
        );

        collectionRecordId = matches?.[0]?.id;

        if (!collectionRecordId) {
          throw [{ message: "Collection not found" }];
        }
      }

      const getCollectionResponse = await api.getCollection(
        collectionRecordId,
        apiFieldsToFetch,
      );
      pushToGoogleAnalytics({
        event: "pageContentLoaded",
        title:
          getTranslation(getCollectionResponse, {
            key: "languages_code",
            code: language.code,
          })?.title || "",
      });

      setCollection(getCollectionResponse);

      setBeefCuts(
        getCollectionResponse.items
          ?.filter((junction) => junction.collection === "beef_product")
          ?.map((junction) => junction.item),
      );

      setCuisines(
        getCollectionResponse.items
          ?.filter((junction) => junction.collection === "collections")
          ?.map((junction) => junction.item),
      );

      setRecipes(
        getCollectionResponse.items
          ?.filter((junction) => junction.collection === "recipe")
          ?.map((junction) => junction.item),
      );

      setRecipeAuthors(
        getCollectionResponse.items
          ?.filter((junction) => junction.collection === "recipe_author")
          ?.map((junction) => junction.item)
          ?.filter((individual) => individual.status === "published"),
      );
    } catch (error) {
      error?.map((err) => notify("error", err.message));
    } finally {
      setLoading(false);
    }
  }, [slug]);

  if (loading) {
    return <Loader />;
  }

  return (
    <Box className="collection">
      <Container maxWidth disableGutters sx={{ p: 0 }}>
        <Box>
          <PageHeader
            item={{
              name: getTranslation(collection, {
                key: "languages_code",
                code: language.code,
              }).title,
              recipe: [],
              svg_icon: collection.icon,
            }}
            useAsLink={false}
          />
        </Box>
      </Container>

      <Container
        sx={{
          my: 5,
        }}
      >
        {collection.type === "CUISINE" && (
          <Cuisine
            title={
              getTranslation(collection, {
                key: "languages_code",
                code: language.code,
              }).title
            }
            description={
              getTranslation(collection, {
                key: "languages_code",
                code: language.code,
              }).content
            }
            recipeAuthors={recipeAuthors}
            beefCuts={beefCuts}
            recipes={recipes}
            imageUrl={
              getTranslation(collection, {
                key: "languages_code",
                code: language.code,
              }).image
                ? `${process.env.REACT_APP_API_URL}/assets/${getTranslation(collection, { key: "languages_code", code: language.code }).image}?key=page-hero-image`
                : null
            }
          />
        )}

        {collection.type === "CUISINES" && (
          <Cuisines
            title={
              getTranslation(collection, {
                key: "languages_code",
                code: language.code,
              }).title
            }
            description={
              getTranslation(collection, {
                key: "languages_code",
                code: language.code,
              }).content
            }
            cuisines={cuisines}
            imageUrl={`${process.env.REACT_APP_API_URL}/assets/${getTranslation(collection, { key: "languages_code", code: language.code }).image}?key=page-hero-image`}
          />
        )}

        {collection.type === "BEEF_CUT" && (
          <Collection id={collection.id} content />
        )}

        {!!!collection.type && <Collection id={collection.id} content />}
      </Container>
    </Box>
  );
}

export default CollectionPage;
